import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  SafeAreaView,
  ActivityIndicator,
  Platform,
  Vibration,
} from 'react-native';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../navigation/RootNavigator';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import Icon from 'react-native-vector-icons/Ionicons';
import * as Haptics from 'expo-haptics';
import logger from '../utils/logger';

type ScanBarcodeNavigationProp = StackNavigationProp<RootStackParamList, 'ScanBarcode'>;

export default function ScanBarcodeScreen() {
  const navigation = useNavigation<ScanBarcodeNavigationProp>();
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [permission, requestPermission] = useCameraPermissions();
  const [scanned, setScanned] = useState(false);
  const [scanning, setScanning] = useState(true);

  useEffect(() => {
    requestCameraPermissionIfNeeded();
  }, [permission]);

  const requestCameraPermissionIfNeeded = async () => {
    try {
      if (!permission) {
        return;
      }

      if (!permission.granted && permission.canAskAgain) {
        await requestPermission();
      }
    } catch (error) {
      logger.error('Camera permission error:', error);
    }
  };

  const handleBarCodeScanned = async ({ type, data }: { type: string; data: string }) => {
    if (scanned) return;

    // Vibrate on successful scan
    if (Platform.OS === 'ios') {
      await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } else {
      Vibration.vibrate(100);
    }

    setScanned(true);
    setScanning(false);

    logger.log('📱 Barcode scanned:', { type, data, length: data.length });

    // Validate serial number format (20 digits)
    if (!/^\d{20}$/.test(data)) {
      Alert.alert(t('invalid_serial'), t('serial_must_be_20_digits'), [
        {
          text: t('scan_again'),
          onPress: () => {
            setScanned(false);
            setScanning(true);
          },
        },
        { text: t('cancel'), onPress: () => navigation.goBack() },
      ]);
      return;
    }

    // Navigate to detection result screen
    navigation.navigate('DetectionResult', { serialNumber: data });
  };

  const toggleScanning = () => {
    setScanned(false);
    setScanning(!scanning);
  };

  if (!permission) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.colors.text }]}>
          {t('requesting_camera_permission')}
        </Text>
      </SafeAreaView>
    );
  }

  if (!permission.granted) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.errorContainer}>
          <Icon name="camera-off" size={64} color={theme.colors.error} />
          <Text style={[styles.errorTitle, { color: theme.colors.text }]}>
            {t('camera_permission_denied')}
          </Text>
          <Text style={[styles.errorMessage, { color: theme.colors.textSecondary }]}>
            {t('camera_permission_required_message')}
          </Text>
          <TouchableOpacity
            style={[styles.button, { backgroundColor: theme.colors.primary }]}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.buttonText}>{t('go_back')}</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: '#000' }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: 'rgba(0,0,0,0.7)' }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name={isRTL ? 'chevron-forward' : 'chevron-back'} size={28} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('scan_sensor_barcode')}</Text>
        <View style={{ width: 40 }} />
      </View>

      {/* Camera View */}
      <View style={styles.cameraContainer}>
        {scanning ? (
          <CameraView
            style={styles.camera}
            facing="back"
            onBarcodeScanned={scanned ? undefined : handleBarCodeScanned}
            barcodeScannerSettings={{
              barcodeTypes: ['code128', 'ean13', 'qr'],
            }}
          >
            {/* Scanning Frame Overlay */}
            <View style={styles.overlay}>
              {/* Top */}
              <View style={styles.overlaySection} />

              {/* Middle with scanning frame */}
              <View style={styles.middleSection}>
                <View style={styles.overlaySection} />

                <View style={[styles.scanFrame, { borderColor: theme.colors.success }]}>
                  {/* Corner indicators */}
                  <View
                    style={[styles.corner, styles.topLeft, { borderColor: theme.colors.success }]}
                  />
                  <View
                    style={[styles.corner, styles.topRight, { borderColor: theme.colors.success }]}
                  />
                  <View
                    style={[
                      styles.corner,
                      styles.bottomLeft,
                      { borderColor: theme.colors.success },
                    ]}
                  />
                  <View
                    style={[
                      styles.corner,
                      styles.bottomRight,
                      { borderColor: theme.colors.success },
                    ]}
                  />

                  {/* Scanning line animation */}
                  <View
                    style={[
                      styles.scanLine,
                      { backgroundColor: theme.colors.success, shadowColor: theme.colors.success },
                    ]}
                  />
                </View>

                <View style={styles.overlaySection} />
              </View>

              {/* Bottom */}
              <View style={styles.overlaySection} />
            </View>
          </CameraView>
        ) : (
          <View style={styles.pausedOverlay}>
            <Icon name="pause-circle" size={80} color="#fff" />
            <Text style={styles.pausedText}>{t('scanning_paused')}</Text>
          </View>
        )}
      </View>

      {/* Instructions */}
      <View style={styles.instructions}>
        <View style={styles.instructionCard}>
          <Icon name="barcode-outline" size={32} color={theme.colors.primary} />
          <Text style={[styles.instructionTitle, { color: '#fff' }]}>{t('align_barcode')}</Text>
          <Text style={[styles.instructionText, { color: '#ccc' }]}>
            {t('barcode_scan_instruction')}
          </Text>
        </View>

        {/* Toggle Scanning Button */}
        <TouchableOpacity
          style={[
            styles.toggleButton,
            { backgroundColor: scanning ? '#ff4444' : theme.colors.primary },
          ]}
          onPress={toggleScanning}
        >
          <Icon name={scanning ? 'pause' : 'play'} size={24} color="#fff" />
          <Text style={styles.toggleButtonText}>
            {scanning ? t('pause_scanning') : t('resume_scanning')}
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    zIndex: 10,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
  },
  cameraContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  camera: {
    flex: 1,
  },
  overlay: {
    flex: 1,
  },
  overlaySection: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  middleSection: {
    flexDirection: 'row',
    height: 300,
  },
  scanFrame: {
    width: 300,
    height: 300,
    borderWidth: 2,
    position: 'relative',
    backgroundColor: 'transparent',
  },
  corner: {
    position: 'absolute',
    width: 40,
    height: 40,
  },
  topLeft: {
    top: -2,
    left: -2,
    borderTopWidth: 4,
    borderLeftWidth: 4,
  },
  topRight: {
    top: -2,
    right: -2,
    borderTopWidth: 4,
    borderRightWidth: 4,
  },
  bottomLeft: {
    bottom: -2,
    left: -2,
    borderBottomWidth: 4,
    borderLeftWidth: 4,
  },
  bottomRight: {
    bottom: -2,
    right: -2,
    borderBottomWidth: 4,
    borderRightWidth: 4,
  },
  scanLine: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: 2,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
  },
  pausedOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pausedText: {
    color: '#fff',
    fontSize: 18,
    marginTop: 16,
    fontWeight: '600',
  },
  instructions: {
    padding: 20,
    backgroundColor: 'rgba(0,0,0,0.8)',
  },
  instructionCard: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  instructionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 8,
  },
  instructionText: {
    fontSize: 14,
    marginTop: 4,
    textAlign: 'center',
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    marginTop: 16,
  },
  toggleButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
  button: {
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
